<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid ,reset}" ref="observer">
    <artisio-modal size="lg" :visible="modal.show" :data="model" @show="onShow" @hidden="onHidden"
                   :title="modalTitle" @ok.prevent="handleSubmit(onSubmit)" :show-confirmation="showConfirmation"
                   :ok-title="$t('Submit')" :is-loading="loading" modal-class="mobile-full-screen">
      <div class="row">
        <div class="col-md-12">
          <table class="info-table table table-sm settlement-payments-table ">
            <tbody>
            <tr>
              <th class="border-top-0">{{ $t('Settlement') }}</th>
              <th class="border-top-0">{{ $t('Amount') }}</th>
              <th class="border-top-0" v-if="currenciesDiffer">
                {{ $t('Converted Amount') }}
              </th>
            </tr>
            <tr v-for="settlementPayment in model.settlement_payments"
                :key="`settlement-row-${settlementPayment.uuid}`">
              <td>
                <b>{{ settlementPayment.settlement.settlement_no }}</b>
              </td>
              <td>
                <currency-input-widget v-if="model.settlement_payments && model.settlement_payments.length > 1"
                                       :currency="currencySymbol" :label="false" :model="settlementPayment"
                                       attribute="amount"  @input="onAmountChange(settlementPayment)"
                />
                <currency-input-widget
                  v-else-if="payableInfo && model.settlement_payments && model.settlement_payments.length === 1"
                  :currency="currencySymbol" :label="false" :model="settlementPayment" @input="onAmountChange(settlementPayment)"
                  attribute="amount" :rules="type === 'refund' ? 'required' : 'required|min_value:0'"
                />
                <div v-if="!model.uuid && amountExceeded && type === 'payment'" class="error-note">
                  {{
                    $t('Amount entered is more than the payable amount, please note some items within this settlement might be selected as on hold, are you sure you would like to proceed?')
                  }}
                </div>
              </td>
              <td v-if="currenciesDiffer">
                <currency-input-widget :currency="settlementCurrencySymbol" :label="false"
                                       :model="settlementPayment" attribute="converted_amount"
                                       @input="onConvertedAmountChange(settlementPayment)"/>
              </td>

            </tr>
            <tr v-if="model.settlement_payments && model.settlement_payments.length > 1">
              <td :colspan="currenciesDiffer ? 3 : 2" class="text-right border-top-0 pb-0">
                <span class="d-inline-block mr-3"><b>{{ $t('Total Amount') }}</b></span>
                <span>{{ totalAmount | asCurrency(currencySymbol) }}</span>
              </td>
            </tr>
            <tr v-else-if="payableInfo && model.settlement_payments && model.settlement_payments.length === 1">
              <td :colspan="currenciesDiffer ? 3 : 2" class="text-right border-top-0 pb-0">
                <div>
                  <b>{{ $t('Total Amount') }}</b>: {{ payableInfo.total_amount | asCurrency(currencySymbol) }}
                </div>
                <div class="mt-2" v-if="type==='payment'">
                  <b>{{ $t('Payable Amount') }}</b>: {{ payableInfo.payable_amount | asCurrency(currencySymbol) }}
                </div>
                <div v-if="type==='payment'">
                  <div class="mt-2">
                    <b>{{ $t('Items On Hold') }}</b>: {{ payableInfo.items_on_hold }}
                  </div>
                  <div class="mt-2">
                    <b>{{ $t('Total Amount for Items On Hold') }}</b>:
                    {{ payableInfo.total_amount_on_hold | asCurrency(currencySymbol) }}
                  </div>
                  <div class="mt-2">
                    <b>{{ $t('Total Amount Paid for by the Buyers (Inc. On Hold)') }}</b>:
                    {{ payableInfo.total_paid_by_buyers | asCurrency(currencySymbol) }}
                  </div>
                  <div class="mt-2">
                    <b>{{ $t('Total Amount Paid for by the Buyers (Excl. On Hold)') }}</b>:
                    {{ payableInfo.total_paid_by_buyers_excl_on_hold | asCurrency(currencySymbol) }}
                  </div>
                </div>

              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
      <hr class="mt-2">
      <div class="row">
        <template v-if="totalSettlementsIsOne && beneficiaries && beneficiaries.length">
          <div class="col-md-6">
            <input-widget :model="model" attribute="beneficiary_relation_uuid" value-field="uuid" type="select2"
                          display-field="${item.receipt_no} - ${item.related_customer.display_name} - ${item.beneficiary_rate}%"
                          :items="getBeneficiariesObject"/>
          </div>
        </template>
        <div class="col-md-6">
          <input-widget :model="model" attribute="date" type="datepicker"/>
        </div>
        <div class="col-md-6">
          <input-widget :model="model" attribute="method" value-field="value" display-field="${item.key}" type="select2"
                        :items="paymentMethods"/>
        </div>
        <div class="col-md-6">
          <currency-select-input-widget :model="model" attribute="currency_uuid"
                                        @select="onCurrencySelect" :disabled="!!model.uuid"/>
        </div>
        <div class="col-md-6">
          <input-widget :model="model" attribute="exchange_rate" :rules="exchangeRateRules" type="number"
                        @input="onExchangeRateChange"/>
        </div>
        <div class="col-md-6" v-if="paymentBankAccounts && paymentBankAccounts.length > 0">
          <input-widget :model="model" attribute="bank_account" value-field="value"
                        display-field="${item.key}" type="select2" :items="paymentBankAccounts"/>
        </div>
        <div class="col-md-6">
          <input-widget :model="model" attribute="reference_number" type="text" rules="max:11"/>
        </div>
        <div class="col-md-4">
          <input-widget is-switch :model="model" attribute="reconciled" type="checkbox"/>
        </div>
        <div class="col-md-12">
          <input-widget :model="model" attribute="description" type="textarea"/>
        </div>
      </div>
    </artisio-modal>
  </ValidationObserver>
</template>

<script>
  import InputWidget from "../../core/components/input-widget/InputWidget";
  import {createNamespacedHelpers} from "vuex";
  import ArtisioModal from "../../core/components/artisio-modal/ArtisioModal";
  import CurrencyInputWidget from "../../core/widgets/CurrencyInputWidget";
  import {SOURCE_SETTLEMENT_PAYMENTS_TABLE, SOURCE_SETTLEMENTS_TABLE, TYPE_PAYMENT} from "../constants";
  import SettlementService from "@/modules/settlements/SettlementService";
  import PaymentModel from "@/modules/settlements/Payment.model";
  import SettlementPayment from "@/modules/settlements/SettlementPayment.model";
  import CustomerService from "@/modules/customer/CustomerService";
  import CurrencySelectInputWidget from "@/core/widgets/CurrencySelectInputWidget";

  const {mapState, mapActions} = createNamespacedHelpers('settlements');
  const {mapState: globalMapState} = createNamespacedHelpers('global');
  const {mapActions: settlementPaymentsActions} = createNamespacedHelpers('settlementPayments');

  export default {
    name: "SettlementPaymentFormModal",
    components: {
      CurrencySelectInputWidget,
      CurrencyInputWidget,
      InputWidget,
      ArtisioModal
    },
    data() {
      return {
        loading: false,
        showConfirmation: true,
        model: new PaymentModel(),
        customer: null,
        payableInfo: null,
        beneficiaries: null
      }
    },
    computed: {
      ...globalMapState({
        paymentMethods: state => {
          return state.paymentMethods.filter(pm => pm.value !== 'deposit');
        },
        availablePaymentCurrencies: state => state.availablePaymentCurrencies,
        currencies: state => state.currencies,
        currentBranch: state => state.currentBranch,
        paymentBankAccounts: state => state.paymentBankAccounts,
      }),
      ...mapState({
        modal: state => state.paymentModal,
        editObject: state => state.paymentModal.object,
        type: state => state.paymentModal.type,
        settlements: state => state.paymentModal.settlements,
      }),
      totalAmount() {
        let sum = 0;
        this.model.settlement_payments.forEach(settlementPayment => sum += parseFloat(settlementPayment.amount));
        return sum;
      },
      amountExceeded() {
        return (this.model.settlement_payments.length === 1 &&
          this.payableInfo && this.model.settlement_payments[0].amount > this.payableInfo.payable_amount)
      },
      currenciesDiffer() {
        return this.settlementCurrency && this.currency && this.settlementCurrency.uuid !== this.currency.uuid
      },
      settlementCurrency() {
        if (this.model.settlement_payments && this.model.settlement_payments.length > 0 && this.model.settlement_payments[0].settlement) {
          return this.model.settlement_payments[0].settlement.currency;
        }
        return null
      },
      currency() {
        let tmpCurrency = this.currencies.find(c => c.uuid === this.model.currency_uuid)
        return tmpCurrency ? tmpCurrency : null
      },
      getBeneficiariesObject() {
        return this.beneficiaries;
      },
      totalSettlementsIsOne() {
        return this.settlements && this.settlements.length === 1;
      },
      currencySymbol() {
        return this.currency ? this.currency.symbol : null
      },
      settlementCurrencySymbol() {
        return this.settlementCurrency ? this.settlementCurrency.symbol : null
      },
      modalTitle() {
        if (this.model.uuid) {
          if (this.type === 'refund') {
            return this.$i18n.t(`Update Refund`);
          }
          return this.$i18n.t(`Update Payment`);
        } else {
          if (this.type === 'refund') {
            return this.$i18n.t(`Create Refund`);
          }
          return this.$i18n.t(`Create Payment`);
        }
      },
      exchangeRateRules() {
        return this.currenciesDiffer ? 'required' : ''
      }
    },
    watch: {
      editObject() {
        if (this.editObject && this.editObject.uuid) {
          this.model = new PaymentModel(this.editObject);
        }
      },
      async settlements() {
        this.settlements.forEach(settlement => {
          if (!this.model.currency_uuid) {
            this.model.currency_uuid = settlement.currency.uuid
          }

          let amount = 0;
          if (this.type === 'payment') {
            amount = parseFloat(settlement.outstanding_amount);
          } else if (this.type === 'refund') {
            amount = parseFloat(settlement.paid_amount * -1);
          }
          this.model.settlement_payments.push(new SettlementPayment({
            amount,
            settlement_uuid: settlement.uuid,
            settlement
          }));
        });

        if (this.settlements.length === 1) {
          const uuid = this.settlements[0].uuid
          let response = await SettlementService.getSettlement(uuid, {expand: "payable_amount,items_on_hold,total_amount_on_hold,total_paid_by_buyers,total_paid_by_buyers_excl_on_hold"})
          if (response.success) {
            this.payableInfo = {
              payable_amount: response.body.payable_amount,
              total_amount: response.body.total_amount,
              items_on_hold: response.body.items_on_hold,
              total_amount_on_hold: response.body.total_amount_on_hold,
              total_paid_by_buyers: response.body.total_paid_by_buyers,
              total_paid_by_buyers_excl_on_hold: response.body.total_paid_by_buyers_excl_on_hold
            }
            if (!this.model.uuid && this.type === 'payment') {
              this.model.settlement_payments[0].amount = response.body.payable_amount
            }
          }
        }
      },
    },
    methods: {
      ...mapActions(['hidePaymentModal', 'getTableData', 'updatePaymentListModalSettlement']),
      ...settlementPaymentsActions({settlementPaymentsGetTableData: 'getTableData'}),

      onShow() {
        this.showConfirmation = true;
        this.initExtraInfo(this.settlements);
        this.getBeneficiaries(this.settlements);
      },
      onHidden() {
        this.payableInfo = null
        this.customer = null;
        this.model = new PaymentModel();
        this.hidePaymentModal();
      },
      async onSubmit() {
        if (this.model.hasError()) {
          return;
        }
        if (this.payableInfo && this.model.settlement_payments.length === 1) {
          const firstPayment = this.model.settlement_payments[0]
          if (this.payableInfo.payable_amount < firstPayment.amount && ['auction', 'manual', 'manual_introductory', 'auction_introductory'].includes(firstPayment.settlement.type)) {
            let success = await this.$confirm(this.$i18n.t("Are you sure you want to pay more than the payable amount of the paid item?"))
            if (!success) {
              return;
            }
          }
        }
        this.loading = true;

        this.model.type = this.type;
        const settlement_payments = this.model.settlement_payments.map(item => {
          return {
            amount: item.amount,
            settlement_uuid: item.settlement_uuid
          }
        });

        let response;

        if (!this.model.uuid) {
          response = await SettlementService.createPayment({
            ...this.model.toJSON(),
            settlement_payments
          });
        } else {
          response = await SettlementService.updatePayment(this.model.uuid, {
            ...this.model.toJSON(),
            settlement_payments
          });
        }

        this.loading = false;

        if (response.success) {
          this.showConfirmation = false;
          this.hidePaymentModal();
          const responseText = this.modal.type === TYPE_PAYMENT
            ? this.$t('Settlement(s) were successfully paid')
            : this.$t('Settlement(s) were successfully refunded')
          this.$toast('success', responseText);
          if (this.modal.source === SOURCE_SETTLEMENTS_TABLE) {
            await this.getTableData();
            this.updatePaymentListModalSettlement();
          } else if (this.modal.source === SOURCE_SETTLEMENT_PAYMENTS_TABLE) {
            await this.settlementPaymentsGetTableData()
          }
        } else {
          if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0]);
          } else {
            this.model.setMultipleErrors(response.body);
          }
        }
      },
      async initExtraInfo(settlements) {
        if (!settlements || settlements.length === 0) {
          return
        }

        let customerUuid = null;

        for (let settlement of settlements) {
          customerUuid = settlement.customer_uuid;
        }

        if (!customerUuid) {
          return;
        }

        let response = await CustomerService.getCustomer(customerUuid, 'term');
        if (response.success) {
          this.customer = response.body;
          if (!this.model.uuid && this.customer.term && this.customer.term.payment_method) {
            this.model.method = this.customer.term.payment_method
          }
        }
      },
      async getBeneficiaries(settlements) {
        this.beneficiaries = await SettlementService.getBeneficiariesBySettlement(settlements[0])
      },
      onCurrencySelect() {
        let tmpCurrency = this.currencies.find(c => c.uuid === this.model.currency_uuid)
        let exchangeRate = null;

        for (let m of this.model.settlement_payments) {
          if (!m.settlement) {
            continue;
          }
          let settlementType = m.settlement.type;

          if (tmpCurrency.uuid === this.settlementCurrency.uuid) {
            exchangeRate = 1
          } else if (settlementType === 'manual' || settlementType === 'service') {
            if (this.currentBranch.currency_exchanges && this.currentBranch.currency_exchanges[tmpCurrency.code]) {
              exchangeRate = this.currentBranch.currency_exchanges[tmpCurrency.code]
            }
          } else if (settlementType === 'auction') {
            if (m.settlement.auction && m.settlement.auction.currency_exchanges && m.settlement.auction.currency_exchanges[tmpCurrency.code]) {
              exchangeRate = m.settlement.auction.currency_exchanges[tmpCurrency.code]
            }
          }

          if (exchangeRate !== null) {
            break
          }
        }

        this.model.exchange_rate = exchangeRate
        this.calculateAmountAll()
      },
      calculateAmountAll() {
        for (let settlementPayment of this.model.settlement_payments) {
          this.calculateAmount(settlementPayment)
        }
      },
      calculateAmount(settlementPayment, amountSource = null) {
        if (amountSource === null) {
          amountSource = settlementPayment.initial_amount
        }

        if (this.model.exchange_rate) {
          settlementPayment.amount = (amountSource / this.model.exchange_rate).toFixed(2)
        }
        settlementPayment.converted_amount = amountSource
      },
      onExchangeRateChange() {
        this.$nextTick(() => {
          this.calculateAmountAll()
        })
      },
      onAmountChange(settlementPayment) {
        if (!this.currenciesDiffer || !this.model.exchange_rate) {
          return true
        }
        this.$nextTick(() => {
          settlementPayment.converted_amount = (settlementPayment.amount * this.model.exchange_rate).toFixed(2)
        })
      },
      onConvertedAmountChange(settlementPayment) {
        this.calculateAmount(settlementPayment, settlementPayment.converted_amount)
      },

    }
  }
</script>

<style lang="scss" scoped>
  .settlement-payments-table {
    tbody {
      td {
        vertical-align: middle;
        padding-top: .7em;
        padding-bottom: .7em;
      }
    }

    /deep/ .form-group {
      margin-bottom: 0;
    }
  }

  .error-note {
    font-size: 12px;
    color: red;
  }
</style>
